import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, CheckCircle2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const TYPES = [
  { v: "pubblica", l: "Pubblica statale" },
  { v: "paritaria", l: "Paritaria" },
  { v: "privata", l: "Privata" },
];

export default function SuggestSchool() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", type: "", address: "", city: "", province: "", postal_code: "",
    region: "", phone: "", email: "", website: "", notes: "",
    contact_email: "", contact_name: "", privacy_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    document.title = "Suggerisci una scuola — Per manina";
    if (user) {
      setForm((f) => ({ ...f, contact_email: f.contact_email || user.email, contact_name: f.contact_name || user.name }));
    }
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) { toast.error("Devi accettare la Privacy Policy"); return; }
    if (!form.type) { toast.error("Seleziona il tipo di scuola"); return; }
    setSubmitting(true);
    try {
      await api.post("/suggestions", form);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio. Riprova.");
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20">
        <div className="card-surface p-10 text-center" data-testid="suggestion-success">
          <CheckCircle2 className="mx-auto text-[#1A362D]" size={48} />
          <h1 className="mt-4 font-display text-3xl text-[#1A362D]">Grazie!</h1>
          <p className="mt-3 font-ui text-[#5C6B64] leading-relaxed">
            La tua segnalazione è arrivata. La verifichiamo entro pochi giorni e la pubblichiamo. Ti rispondiamo via email se serve un chiarimento.
          </p>
          <div className="mt-6 flex gap-2 justify-center">
            <button onClick={() => navigate("/")} className="btn-primary text-sm">Torna alla home</button>
            <button onClick={() => { setSuccess(false); setForm({ ...form, name: "", address: "", city: "", postal_code: "", phone: "", email: "", website: "", notes: "" }); }} className="btn-secondary text-sm">Segnala un'altra scuola</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="suggest-school-page">
      <p className="label-eyebrow text-[#C05C45]">Community</p>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl tracking-tight text-[#1A362D]">
        Non trovi la tua scuola? Aggiungila tu.
      </h1>
      <p className="mt-4 font-ui text-[#5C6B64] leading-relaxed max-w-2xl">
        Il database cresce grazie ai genitori. Bastano due minuti — la nostra moderazione verifica i dati prima di pubblicarli.
      </p>

      {/* Intro narrativa */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-surface p-5">
          <p className="font-display text-xl text-[#1A362D]">Aiuti i genitori della tua zona</p>
          <p className="mt-2 font-ui text-sm text-[#5C6B64] leading-relaxed">
            Molte scuole dell'infanzia — soprattutto le piccole paritarie di paese — non hanno sito né presenza online. Con la tua segnalazione diventano visibili e raggiungibili.
          </p>
        </div>
        <div className="card-surface p-5">
          <p className="font-display text-xl text-[#1A362D]">Verifichiamo prima di pubblicare</p>
          <p className="mt-2 font-ui text-sm text-[#5C6B64] leading-relaxed">
            Ogni segnalazione passa dalla moderazione. Incrociamo i dati con le fonti ufficiali (anagrafe MIUR, FISM, sito della scuola): bastano 2-3 giorni per pubblicare.
          </p>
        </div>
        <div className="card-surface p-5">
          <p className="font-display text-xl text-[#1A362D]">Nessun dato di troppo</p>
          <p className="mt-2 font-ui text-sm text-[#5C6B64] leading-relaxed">
            Ci serve solo la tua email per ricontattarti se mancano informazioni. Niente newsletter, niente spam, niente comunicazioni commerciali. Mai.
          </p>
        </div>
      </div>

      <div className="mt-8 card-surface p-5 bg-[#F2EFEB] border-[#E8E6E1]">
        <p className="label-eyebrow text-[#C05C45]">Cosa è utile inserire</p>
        <ul className="mt-3 space-y-2 font-ui text-sm text-[#1A362D]">
          <li>• <span className="font-medium">Indirizzo preciso</span> — se non sei sicuro/a del numero civico, va benissimo anche un link Google Maps nelle note.</li>
          <li>• <span className="font-medium">Tipo di scuola</span> — la maggior parte delle scuole dell'infanzia private italiane sono <em>paritarie</em>, ovvero riconosciute dal MIUR. Se non sai, indica "Paritaria".</li>
          <li>• <span className="font-medium">Sito web o pagina social</span> — anche un solo link Facebook/Instagram aiuta: lo verifichiamo per estrarre i contatti mancanti.</li>
        </ul>
        <p className="mt-3 font-ui text-xs text-[#5C6B64] italic">
          Se invece i dati di una scuola <em>già presente</em> sono sbagliati o incompleti, dalla scheda della scuola troverai il pulsante "Segnala una correzione".
        </p>
      </div>

      <form onSubmit={submit} className="mt-10 card-surface p-6 sm:p-8 space-y-6" data-testid="suggest-form">
        {/* Dati scuola */}
        <section className="space-y-4">
          <p className="label-eyebrow text-[#1A362D]">Dati della scuola</p>

          <div>
            <label className="block text-sm font-ui text-[#1A362D] mb-1">Nome scuola *</label>
            <input required value={form.name} onChange={set("name")} data-testid="field-name" placeholder="Es. Scuola dell'Infanzia Don Bosco" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
          </div>

          <div>
            <label className="block text-sm font-ui text-[#1A362D] mb-1">Tipo *</label>
            <div className="flex gap-2 flex-wrap">
              {TYPES.map((t) => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setForm({ ...form, type: t.v })}
                  data-testid={`field-type-${t.v}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-ui border transition-colors ${
                    form.type === t.v ? "bg-[#1A362D] text-white border-[#1A362D]" : "border-[#E8E6E1] text-[#1A362D] hover:border-[#1A362D]"
                  }`}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-ui text-[#1A362D] mb-1">Indirizzo *</label>
            <input required value={form.address} onChange={set("address")} data-testid="field-address" placeholder="Via, numero civico" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-sm font-ui text-[#1A362D] mb-1">Comune *</label>
              <input required value={form.city} onChange={set("city")} data-testid="field-city" placeholder="Es. Milano" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-ui text-[#1A362D] mb-1">Provincia</label>
              <input value={form.province} onChange={set("province")} maxLength={4} data-testid="field-province" placeholder="MI" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white uppercase focus:border-[#C05C45] focus:outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-ui text-[#1A362D] mb-1">CAP</label>
              <input value={form.postal_code} onChange={set("postal_code")} data-testid="field-postal" placeholder="20121" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-ui text-[#1A362D] mb-1">Regione</label>
              <input value={form.region} onChange={set("region")} data-testid="field-region" placeholder="Lombardia" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
            </div>
          </div>
        </section>

        {/* Contatti scuola */}
        <section className="space-y-4">
          <p className="label-eyebrow text-[#1A362D]">Contatti della scuola <span className="text-[#8A9691] normal-case tracking-normal">(facoltativi)</span></p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-ui text-[#1A362D] mb-1">Telefono</label>
              <input value={form.phone} onChange={set("phone")} data-testid="field-phone" placeholder="02 1234567" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-ui text-[#1A362D] mb-1">Email</label>
              <input type="email" value={form.email} onChange={set("email")} data-testid="field-email" placeholder="segreteria@scuola.it" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-ui text-[#1A362D] mb-1">Sito web</label>
            <input value={form.website} onChange={set("website")} data-testid="field-website" placeholder="https://www.scuolaesempio.it" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-ui text-[#1A362D] mb-1">Note libere</label>
            <textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="field-notes" placeholder="Aggiungi info utili che vuoi farci sapere (es. indicazioni, link Google Maps, particolarità della scuola...)" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
          </div>
        </section>

        {/* Tuoi contatti */}
        <section className="space-y-4">
          <p className="label-eyebrow text-[#1A362D]">I tuoi contatti</p>
          <p className="font-ui text-xs text-[#5C6B64]">Ti useremo questi dati solo per ricontattarti se serve un chiarimento. Niente newsletter.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-ui text-[#1A362D] mb-1">Nome</label>
              <input value={form.contact_name} onChange={set("contact_name")} data-testid="field-contact-name" placeholder="Mario" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-ui text-[#1A362D] mb-1">La tua email *</label>
              <input required type="email" value={form.contact_email} onChange={set("contact_email")} data-testid="field-contact-email" placeholder="tu@email.it" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
            </div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={form.privacy_consent} onChange={set("privacy_consent")} data-testid="field-privacy" className="mt-1 accent-[#C05C45]" />
            <span className="font-ui text-xs text-[#5C6B64] leading-relaxed">
              Accetto il trattamento dei dati come da <a href="/info/privacy" target="_blank" rel="noreferrer" className="underline text-[#C05C45]">Privacy Policy</a> e confermo che i dati forniti sono veritieri.
            </span>
          </label>
        </section>

        <div className="flex gap-2">
          <button type="submit" disabled={submitting} data-testid="submit-suggestion" className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
            <Send size={14} /> {submitting ? "Invio…" : "Invia segnalazione"}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary text-sm">Annulla</button>
        </div>
      </form>
    </div>
  );
}

export function MissingSchoolCTA({ city }) {
  return (
    <div className="card-surface p-6 bg-[#F2EFEB] border-[#C05C45]/30 flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-[#C05C45] text-white flex items-center justify-center flex-shrink-0"><Plus size={20} /></div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-lg text-[#1A362D]">Non trovi la tua scuola{city ? ` a ${city}` : ""}?</p>
        <p className="mt-1 font-ui text-sm text-[#5C6B64]">Aiutaci a completare il database — bastano due minuti.</p>
      </div>
      <a href="/suggerisci" data-testid="missing-school-cta" className="btn-primary text-sm whitespace-nowrap self-center">Suggerisci scuola</a>
    </div>
  );
}
